import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import TextInput from "../components/forms/TextInput";
import SelectInput from "../components/forms/SelectInput";

import {
  getPreauthSession,
  clearPreauthSession,
} from "../components/helpers/authFlowStorage";

const shellStyle = {
  width: "100%",
  maxWidth: "480px",
  margin: "0 auto",
  paddingTop: "var(--space-6)",
};

const footerStyle = {
  textAlign: "center",
};

const backButtonStyle = {
  border: 0,
  padding: 0,
  background: "transparent",
  color: "var(--color-accent)",
  font: "inherit",
  fontWeight: "var(--font-weight-semibold)",
  cursor: "pointer",
};

const ExcursionCreate = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { applySession } = useAuth();
  const { phoneNumber, preauthToken, name, surname } = getPreauthSession();

  const [form, setForm] = useState({
    short_name: "",
    definition: "",
    start_date: "",
    end_date: "",
    excursion_currency: "TRL",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [loginCompleted, setLoginCompleted] = useState(false);

  useEffect(() => {
    if (loginCompleted) return;

    if (!phoneNumber || !preauthToken) {
      navigate("/login", { replace: true });
    }
  }, [phoneNumber, preauthToken, navigate, loginCompleted]);

  const handleInput = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    if (!phoneNumber) return t("excursion_create.errors.no_phone");
    if (!preauthToken) return t("excursion_create.errors.no_session");
    if (!name.trim() || !surname.trim()) {
      return t("excursion_create.errors.no_name");
    }
    if (!form.short_name.trim()) return t("excursion_create.errors.no_short_name");
    if (!form.start_date) return t("excursion_create.errors.no_start_date");
    if (!form.end_date) return t("excursion_create.errors.no_end_date");
    if (form.end_date < form.start_date) {
      return t("excursion_create.errors.invalid_date");
    }
    return null;
  };

  const handleCreate = async () => {
    if (isLoading) return;

    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    try {
      setIsLoading(true);

      const createRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/excursion-create`,
        {
          phone_number: phoneNumber,
          name: name.trim(),
          surname: surname.trim(),
          short_name: form.short_name.trim(),
          definition: form.definition.trim() || null,
          start_date: form.start_date,
          end_date: form.end_date,
          excursion_currency: form.excursion_currency,
        }
      );

      const { app_id } = createRes.data || {};

      if (!app_id) {
        throw new Error("Backend did not return app_id.");
      }

      const loginRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/login-final`,
        {
          app_id,
          preauth_token: preauthToken,
        }
      );

      const accessToken = loginRes.data?.access_token;
      const subscriptionStatus = String(
        loginRes.data?.subscription_status || ""
      ).toLowerCase();

      if (!accessToken) {
        throw new Error("Backend did not return access_token.");
      }

      localStorage.setItem("token", accessToken);
      localStorage.setItem("role", "admin");
      localStorage.setItem("active_app_id", app_id);
      localStorage.setItem("subscription_status", subscriptionStatus);

      setLoginCompleted(true);
      clearPreauthSession();

      if (subscriptionStatus === "pending") {
        navigate("/pending", { replace: true });
        return;
      }

      navigate("/login", { replace: true });
    } catch (error) {
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        t("excursion_create.errors.create_failed");

      if (
        msg === "Invalid or expired pre-auth token" ||
        msg === "Invalid pre-auth token type" ||
        msg === "Invalid pre-auth token subject" ||
        msg === "Phone mismatch"
      ) {
        clearPreauthSession();
        alert(t("excursion_create.errors.session_expired"));
        navigate("/login", { replace: true });
        return;
      }

      alert(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    navigate("/excursion-gate", { replace: true });
  };

  if (!phoneNumber || !preauthToken) return null;

  return (
    <StandardScreen background="default">
      <ScreenContent>
        <div style={shellStyle}>
          <Card
            title={t("excursion_create.title")}
            subtitle={t("excursion_create.subtitle")}
          >
            <Stack gap="4">
              <FormField label={t("excursion_create.fields.name")}>
                <TextInput
                  type="text"
                  name="short_name"
                  value={form.short_name}
                  onChange={handleInput}
                  placeholder={t("excursion_create.placeholders.name")}
                  disabled={isLoading}
                />
              </FormField>

              <FormField label={t("excursion_create.fields.definition")}>
                <TextInput
                  type="text"
                  name="definition"
                  value={form.definition}
                  onChange={handleInput}
                  placeholder={t("excursion_create.placeholders.definition")}
                  disabled={isLoading}
                />
              </FormField>

              <FormField label={t("excursion_create.fields.start_date")}>
                <TextInput
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleInput}
                  disabled={isLoading}
                />
              </FormField>

              <FormField label={t("excursion_create.fields.end_date")}>
                <TextInput
                  type="date"
                  name="end_date"
                  value={form.end_date}
                  onChange={handleInput}
                  disabled={isLoading}
                />
              </FormField>

              <FormField label={t("excursion_create.fields.currency")}>
                <SelectInput
                  name="excursion_currency"
                  value={form.excursion_currency}
                  onChange={handleInput}
                  disabled={isLoading}
                >
                  <option value="TRL">TRL</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </SelectInput>
              </FormField>

              <Button
                onClick={handleCreate}
                disabled={isLoading}
                fullWidth
                size="lg"
              >
                {isLoading
                  ? t("excursion_create.actions.creating")
                  : t("excursion_create.actions.create")}
              </Button>

              <div style={footerStyle}>
                <button
                  type="button"
                  onClick={goBack}
                  style={backButtonStyle}
                  disabled={isLoading}
                >
                  {t("excursion_create.common.back")}
                </button>
              </div>
            </Stack>
          </Card>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default ExcursionCreate;